/**
 * <TutorDraftComposer> — le brouillon de génération, en bas de la colonne du champion (molecule).
 *
 * Une barre de composition (Wajih, round 4 : « comme un seul bloc qui va ensemble ») : au repos une ligne, le champ
 * et le trombone ; au focus, la carte grandit — le mode en tête, la pièce d'où part la génération, les réglages au
 * format du configurateur (leur nom au-dessus de leur puce, W15), les raccourcis de « similaire », puis le texte — et
 * elle se replie quand rien n'est saisi.
 *
 * **Les quatre entrées arrivent ici, dans le même brouillon, déjà réglé** (Wajih, 04/11 : « il faut que tous ces cas
 * soient ultra fluides ») :
 *  - **le configurateur** : les réglages pré-remplis, chacun déplié sur place (`editor`) ;
 *  - **une image venue de l'extérieur**, glissée n'importe où dans la salle ou collée : sa vignette en tête (`source`) ;
 *  - **une photo du chat** : sa vignette et l'heure du message, le chapitre en cours repris ;
 *  - **« similaire »** : « Comme « Fractions » », et trois raccourcis (même niveau, plus facile, plus dur).
 *
 * Le texte est facultatif : la configuration suffit (`ready`). « / » ouvre la palette des commandes (`palette`, que
 * l'écran filtre sur le texte tapé) ; « Entrée » prend alors la première.
 *
 * État d'interface : le focus et le menu des modes.
 */
import React, { useState } from "react"
import { Image, Platform, Pressable, View, type TextInput } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UITutorSessionSetting, type UITutorSessionSettingField } from "../../../ports/primary/ui/types/tutorLive"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { ImageIcon } from "../../atoms/icons/ImageIcon"
import { Text } from "../../atoms/typography/Text"
import { shadowStyle } from "../../utils/shadow"
import { ActionSheet } from "../ActionSheet"
import { MessageComposer, type ComposerAttachSource } from "../MessageComposer"

/** D'où part la génération : ce que le brouillon montre en tête, et de quoi le retirer. */
export type UITutorDraftSource = {
    kind: "image" | "document" | "chatPhoto" | "similar"
    /** « Image déposée », « Photo du chat · 17:21 », « Comme « Fractions » ». */
    title: string
    /** « fractions.png », « Le chapitre en cours est repris », « Mêmes réglages que l'original ». */
    caption?: string
    thumbUri?: string
    onRemove: () => void
}

export type UITutorDraftModifier = { key: string; label: string; selected: boolean }
export type UITutorDraftCommand = { key: string; label: string; hint: string }

/** Le champ ancré en bas de la session. */
export type TutorSessionComposer = {
    value: string
    onChangeText: (text: string) => void
    onSend: () => void
    /** Ce que fait le champ, honnêtement : en V1 il génère une activité, il ne converse pas. */
    placeholder: string
    sending?: boolean
    /**
     * Ce que fait le champ, dit à l'intérieur du cadre : aujourd'hui « Générer pour Léa » seul ; à la cible,
     * d'autres modes (`options`, un menu) s'ajoutent là sans rien déplacer.
     */
    mode?: UITutorComposerMode
    /** Les réglages pré-remplis : la génération. Absents : le champ écrit au champion (étape 2). */
    settings?: UITutorSessionSetting[]
    onEditSetting?: (field: UITutorSessionSettingField) => void
    /** Le champ prend le focus : l'écran y prépare la génération (ses réglages paraissent d'emblée). */
    onFocus?: () => void
    /**
     * Le réglage touché, déplié sur place au-dessus du champ (`<TutorSessionSettingSheet inline>`) : pas de
     * feuille ni de voile, la session reste lisible. Absent : rien.
     */
    editor?: React.ReactNode
    /**
     * La barre dépliée : le mode, le texte et la rangée d'options. Absent : elle se déplie au focus et se
     * replie quand rien n'est saisi (une story la force).
     */
    open?: boolean
    /** Le trombone : une photo ou un PDF d'exercice. */
    attachSources?: ComposerAttachSource[]
    onAttach?: (source: ComposerAttachSource) => void
    /**
     * La configuration suffit à partir, sans texte (Wajih : « pas forcément besoin de taper un texte
     * d'instructions, juste configurer suffit ») : l'« Entrée » paraît. Défaut : le texte seul la fait paraître.
     */
    ready?: boolean
    /** La pièce d'où part la génération : une image, une photo du chat, l'activité dont on fait une semblable. */
    source?: UITutorDraftSource
    /** Les raccourcis de « similaire » : même niveau, plus facile, plus dur. */
    modifiers?: UITutorDraftModifier[]
    onModifier?: (key: string) => void
    /** La palette ouverte par « / » : les commandes que le texte appelle (« Entrée » prend la première). */
    palette?: UITutorDraftCommand[] | null
    onCommand?: (key: string) => void
    /** La ref du champ (le collage d'un support au web, écouté sur le champ). Absente : aucune. */
    inputRef?: React.MutableRefObject<TextInput | null>
}

/** Le mode du champ : son mot et son glyphe ; plusieurs `options` en font un menu. */
export type UITutorComposerMode = {
    label: string
    iconName: IconName
    options?: { key: string; label: string; iconName: IconName }[]
    onChange?: (key: string) => void
    /** Le menu des modes ouvert (une story). Absent : il s'ouvre au toucher. */
    menuOpen?: boolean
}

export type TutorDraftComposerProps = { composer: TutorSessionComposer; testID?: string }

const TEXTS = {
    modes: (label: string) => `${label}, changer ce que fait le champ`,
    modesTitle: "Que veux-tu faire ?",
    remove: (title: string) => `Retirer ${title}`,
    edit: (label: string) => `${label}, changer`,
    choose: "Choisir",
    detail: "Une consigne en plus ? (facultatif)",
    commands: "Commandes",
    slash: "pour les commandes",
}

/** Le glyphe d'un réglage quand il n'a pas d'image à lui ; la matière et le type ont la leur (`iconUri`). */
const SETTING_ICON: Record<UITutorSessionSettingField, IconName> = {
    class: "graduation-cap",
    subject: "book-open",
    chapter: "book-open",
    level: "sliders-h",
    duration: "clock",
    skills: "tasks",
    personalize: "heart",
}
/**
 * Le nom d'un réglage, au-dessus de sa puce (weekly du 03/11, W15 : la barre est un configurateur, on règle
 * puis on génère ; le texte n'est qu'un plus). Une puce encore vide dit « Choisir » sous lui.
 */
const SETTING_CAPTION: Record<UITutorSessionSettingField, string> = {
    class: "Classe",
    subject: "Matière",
    chapter: "Chapitre",
    level: "Type",
    duration: "Durée",
    skills: "Compétences",
    personalize: "Personnaliser",
}
const SOURCE_ICON: Record<UITutorDraftSource["kind"], IconName> = {
    image: "file-image",
    document: "file-pdf",
    chatPhoto: "comment-dots",
    similar: "copy",
}
/** L'icône d'une option de la rangée : toutes à la même taille, image ou glyphe. */
const OPTION_ICON = 16
const THUMB = 44

export function TutorDraftComposer(props: TutorDraftComposerProps) {
    const { composer, testID } = props
    // État d'interface : le champ a le focus (un appui dans la barre ne le lui retire pas, `MessageComposer`).
    const [focused, setFocused] = useState(false)
    const [modesOpen, setModesOpen] = useState(false)
    const open =
        composer.open ??
        (focused ||
            composer.value.trim() !== "" ||
            composer.source != null ||
            composer.sending === true ||
            composer.editor != null)
    const mode = composer.mode
    const modeOptions = mode?.options ?? []
    const palette = composer.palette ?? null

    const toolbar =
        open && composer.settings != null ? (
            // Les réglages passent à la ligne : ils se lisent et se touchent sans défiler (Wajih, live en prod du 03/11).
            <View style={styles.settings}>
                {composer.settings.map((setting) => (
                    <View key={setting.field} style={styles.setting}>
                        <Text size="text-xs" weight="semibold" color="text-muted" numberOfLines={1}>
                            {SETTING_CAPTION[setting.field]}
                        </Text>
                        <ChoiceChip
                            label={setting.missing ? TEXTS.choose : setting.label}
                            action
                            // Déplié sur place, ou une bascule allumée : la puce le dit (et le lecteur d'écran avec elle).
                            selected={setting.open === true || setting.on === true}
                            leading={
                                <View style={styles.optionIcon}>
                                    {setting.iconUri != null && !setting.missing ? (
                                        <ImageIcon uri={setting.iconUri} size={OPTION_ICON} />
                                    ) : (
                                        <Icon
                                            name={setting.missing ? "plus" : SETTING_ICON[setting.field]}
                                            size={OPTION_ICON}
                                            color={setting.on === true ? "role" : "text-primary"}
                                            solid={setting.on === true}
                                        />
                                    )}
                                </View>
                            }
                            onPress={() => composer.onEditSetting?.(setting.field)}
                            accessibilityLabel={TEXTS.edit(`${SETTING_CAPTION[setting.field]} : ${setting.label}`)}
                        />
                    </View>
                ))}
            </View>
        ) : undefined

    // Le configurateur : le mode, la pièce, puis les réglages AU-DESSUS du champ (W15), puis les raccourcis.
    const header = open ? (
        <Stack gap="3" style={styles.barHead}>
            {mode != null && (
                <Row align="center" justify="between">
                    <Row gap="1" align="center" style={styles.grow}>
                        <Icon name={mode.iconName} size={OPTION_ICON} color="role" />
                        {modeOptions.length > 1 ? (
                            <Button
                                label={mode.label}
                                iconName="chevron-down"
                                iconPosition="right"
                                variant="ghost"
                                size="sm"
                                onPress={() => setModesOpen(true)}
                                accessibilityLabel={TEXTS.modes(mode.label)}
                            />
                        ) : (
                            <Text size="text-sm" weight="bold" color="role-strong" numberOfLines={1}>
                                {mode.label}
                            </Text>
                        )}
                    </Row>
                    {composer.onCommand != null && (
                        <Row gap="1" align="center">
                            <Kbd label="/" typed />
                            <Text size="text-xs" color="text-muted">
                                {TEXTS.slash}
                            </Text>
                        </Row>
                    )}
                </Row>
            )}
            {composer.source != null && <SourcePiece source={composer.source} />}
            {toolbar}
            {composer.modifiers != null && composer.modifiers.length > 0 && (
                <View style={styles.modifiers}>
                    {composer.modifiers.map((modifier) => (
                        <ChoiceChip
                            key={modifier.key}
                            label={modifier.label}
                            selected={modifier.selected}
                            variant="pill"
                            onPress={() => composer.onModifier?.(modifier.key)}
                        />
                    ))}
                </View>
            )}
        </Stack>
    ) : undefined

    return (
        <View style={styles.anchor} testID={testID}>
            {palette != null && palette.length > 0 && (
                <View style={styles.palette} accessibilityRole="menu">
                    <Text size="text-xxs" weight="bold" color="text-muted" uppercase style={styles.paletteTitle}>
                        {TEXTS.commands}
                    </Text>
                    {palette.map((command, position) => (
                        <Pressable
                            key={command.key}
                            onPress={() => composer.onCommand?.(command.key)}
                            // Le survol (web) éclaire la commande ; la première est celle que prend « Entrée ».
                            style={(state) =>
                                styles.command(position === 0 || (state as { hovered?: boolean }).hovered === true)
                            }
                            accessibilityRole="menuitem"
                        >
                            <Stack gap="0" style={styles.grow}>
                                <Text size="text-sm" weight="semibold" color="text-strong">
                                    {command.label}
                                </Text>
                                <Text size="text-xs" color="text-muted" numberOfLines={1}>
                                    {command.hint}
                                </Text>
                            </Stack>
                            {position === 0 && <Kbd label="↵" />}
                        </Pressable>
                    ))}
                </View>
            )}
            {composer.editor != null && <View style={styles.editor}>{composer.editor}</View>}
            <MessageComposer
                inputRef={composer.inputRef}
                value={composer.value}
                onChangeText={composer.onChangeText}
                onSend={composer.onSend}
                // Les réglages au-dessus du champ : le texte n'est qu'une précision (weekly du 03/11, W15).
                placeholder={toolbar != null ? TEXTS.detail : composer.placeholder}
                sending={composer.sending}
                attachSources={composer.attachSources}
                attachIcon="paperclip"
                onAttach={composer.onAttach}
                ready={composer.ready === true || composer.source != null || (palette != null && palette.length > 0)}
                sendIcon="level-down-alt"
                sendOnlyWhenReady
                header={header}
                onFocusChange={(next) => {
                    setFocused(next)
                    if (next) composer.onFocus?.()
                }}
            />
            {modeOptions.length > 1 && (
                <ActionSheet
                    visible={mode?.menuOpen ?? modesOpen}
                    onClose={() => setModesOpen(false)}
                    title={TEXTS.modesTitle}
                    items={modeOptions.map((choice) => ({
                        icon: choice.iconName,
                        label: choice.label,
                        onPress: () => mode?.onChange?.(choice.key),
                    }))}
                />
            )}
        </View>
    )
}

/** La pièce d'où part la génération : sa vignette (ou son glyphe), son nom, et de quoi la retirer. */
function SourcePiece(props: { source: UITutorDraftSource }) {
    const { source } = props
    const icon = SOURCE_ICON[source.kind]
    return (
        <View style={styles.piece}>
            {source.thumbUri != null ? (
                <Image source={{ uri: source.thumbUri }} style={styles.thumb} />
            ) : (
                <View style={[styles.thumb, styles.thumbIcon]}>
                    <Icon name={icon} size={20} color="role" />
                </View>
            )}
            <Stack gap="0" style={styles.grow}>
                <Row gap="1" align="center">
                    <Icon name={icon} size={12} color="role" />
                    <Text size="text-sm" weight="semibold" color="text-strong" numberOfLines={1} style={styles.grow}>
                        {source.title}
                    </Text>
                </Row>
                {source.caption != null && (
                    <Text size="text-xs" color="text-muted" numberOfLines={1}>
                        {source.caption}
                    </Text>
                )}
            </Stack>
            <IconButton
                iconName="times"
                appearance="transparent"
                iconColor="text-muted"
                size={28}
                iconSize={14}
                onPress={source.onRemove}
                accessibilityLabel={TEXTS.remove(source.title)}
            />
        </View>
    )
}

/** Une touche du clavier, dite à côté de son geste : le tuteur est surtout au web. */
/**
 * Une touche du clavier, dite à côté du geste qu'elle fait. Échap et Entrée n'existent qu'au clavier d'un
 * ordinateur : sur une tablette tactile, la touche ne se montre pas. `typed` : un caractère qui se tape aussi
 * au clavier de l'écran (« / »), montré partout.
 */
export function Kbd(props: { label: string; typed?: boolean }) {
    if (props.typed !== true && Platform.OS !== "web") return null
    return (
        <View style={styles.kbd}>
            <Text size="text-xxs" weight="bold" color="text-muted">
                {props.label}
            </Text>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    anchor: { position: "relative" as const },
    grow: { flex: 1, minWidth: 0 },
    editor: { marginBottom: theme.spacing["2"] },
    settings: { flexDirection: "row", flexWrap: "wrap", columnGap: theme.spacing["2"], rowGap: theme.spacing["2"] },
    /** Un réglage : son nom au-dessus, sa puce dessous (le configurateur). */
    setting: { gap: theme.spacing["1"], maxWidth: "100%" },
    modifiers: { flexDirection: "row" as const, flexWrap: "wrap" as const, gap: theme.spacing["2"] },
    /** Le mode, la pièce et les réglages, en tête de la barre : alignés sur le texte. */
    barHead: { paddingHorizontal: theme.spacing["2"], paddingTop: theme.spacing["1"] },
    /** L'icône d'une option, dans le retrait d'un visage : le même air des deux côtés du libellé. */
    optionIcon: { paddingLeft: theme.spacing["2"] },
    piece: {
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["3"],
        padding: theme.spacing["2"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-subtle"],
    },
    thumb: { width: THUMB, height: THUMB, borderRadius: theme.radius.sm },
    thumbIcon: {
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors["border-subtle"],
    },
    kbd: {
        minWidth: 20,
        height: 20,
        paddingHorizontal: theme.spacing["1"],
        alignItems: "center" as const,
        justifyContent: "center" as const,
        borderRadius: theme.radius.sm,
        borderWidth: 1,
        borderBottomWidth: 2,
        borderColor: theme.colors.border,
        backgroundColor: theme.colors.surface,
    },
    palette: {
        position: "absolute" as const,
        left: 0,
        right: 0,
        bottom: "100%",
        marginBottom: theme.spacing["2"],
        padding: theme.spacing["2"],
        gap: theme.spacing["1"],
        borderRadius: theme.radius.lg,
        backgroundColor: theme.colors["surface-floating"],
        borderWidth: 1,
        borderColor: theme.colors["border-subtle"],
        zIndex: 2,
        ...shadowStyle(theme, "floating"),
    },
    paletteTitle: { paddingHorizontal: theme.spacing["2"], paddingVertical: theme.spacing["1"] },
    command: (active: boolean) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["3"],
        paddingHorizontal: theme.spacing["2"],
        paddingVertical: theme.spacing["2"],
        borderRadius: theme.radius.md,
        backgroundColor: active ? theme.colors["surface-subtle"] : "transparent",
    }),
}))
